/** Hazard levels (04 §9), the Quartermaster's unlock catalogue (04 §5) and epitaphs for the wall. */

import { CACHE_FIND, COMPANY, STANDARD_ISSUE } from './economy';

/** The permanent rules of a run. A hazard level applies its own row and every row below it. */
export interface HazardRules {
  readinessStart: number;
  standardMagazines: number;
  greenRecruits: boolean;
  fewerSupportNodes: number;
  keystoneModifier: boolean;
  startRp: number;
  /** Extracting quietly still cools the war down. */
  quietCools: boolean;
  extraRecovery: number;
  rewardScale: number;
  nightOrStorm: boolean;
}

export const NO_HAZARD: HazardRules = {
  readinessStart: 0, standardMagazines: STANDARD_ISSUE.magazines, greenRecruits: false, fewerSupportNodes: 0,
  keystoneModifier: false, startRp: COMPANY.startRp, quietCools: true, extraRecovery: 0, rewardScale: 1,
  nightOrStorm: false,
};

export const HAZARDS: ReadonlyArray<{ level: number; text: string; rules: Partial<HazardRules> }> = [
  { level: 1, text: 'Enemy Readiness starts at 1.', rules: { readinessStart: 1 } },
  { level: 2, text: 'Standard issue loses a magazine.', rules: { standardMagazines: STANDARD_ISSUE.magazines - 1 } },
  { level: 3, text: 'Recruits arrive Green.', rules: { greenRecruits: true } },
  { level: 4, text: 'One fewer support node per theatre.', rules: { fewerSupportNodes: 1 } },
  { level: 5, text: 'Keystones always carry a modifier.', rules: { keystoneModifier: true } },
  { level: 6, text: 'Starting requisition is cut by 10 RP.', rules: { startRp: COMPANY.startRp - 10 } },
  { level: 7, text: 'A quiet extraction no longer lowers Readiness.', rules: { quietCools: false } },
  { level: 8, text: 'Wounds take one more mission to heal.', rules: { extraRecovery: 1 } },
  { level: 9, text: 'Mission rewards pay 15 % less.', rules: { rewardScale: 0.85 } },
  { level: 10, text: 'Every mission is a Night Op or a storm.', rules: { nightOrStorm: true } },
];

export const MAX_HAZARD = HAZARDS.length;

/** The cumulative rules for a hazard level. */
export function hazardRules(level: number): HazardRules {
  let rules = NO_HAZARD;
  for (const h of HAZARDS) if (h.level <= level) rules = { ...rules, ...h.rules };
  return rules;
}

export interface UnlockInfo {
  id: string;
  name: string;
  text: string;
  /** Commendations. */
  cost: number;
}

/**
 * Deliberately small: options and variety, never raw power (04 §5). Supply caches can hold any
 * rare item on any profile; an `armory-<item>` unlock means every operation STARTS with one find
 * of it in stock (`startingStock`).
 */
export const UNLOCKS: readonly UnlockInfo[] = [
  { id: 'veteran-slot-2', name: 'Second Veteran Slot', cost: 40,
    text: 'Bring two Hall of Honor veterans on an operation.' },
  { id: 'fifth-recruit', name: 'Fifth Recruit', cost: 60, text: 'Operations start with five recruits.' },
  { id: 'armory-smoke', name: 'Smoke Grenades', cost: 20,
    text: `Every operation starts with ${CACHE_FIND.smoke} smoke grenades in stock.` },
  { id: 'armory-nvg', name: 'Night-Vision Goggles', cost: 30,
    text: 'Every operation starts with night-vision goggles in stock.' },
  { id: 'armory-wire-cutters', name: 'Wire Cutters', cost: 20,
    text: 'Every operation starts with wire cutters in stock.' },
  { id: 'armory-suppressed-pistol', name: 'Suppressed Pistol', cost: 30,
    text: 'Every operation starts with a suppressed pistol in stock.' },
];

/** `{name}` = surname, `{callsign}`, `{rank}` = abbreviation, `{missions}`, `{kills}`. */
export const EPITAPHS: readonly string[] = [
  '{rank} {name} went in alone, as they all do.',
  'They called {name} "{callsign}". The name stays on the wall.',
  '{missions} drops. One too many.',
  '{callsign} never asked anyone else to go first.',
  'The aircraft waited as long as it could for {name}.',
  '{name} kept the war quiet for as long as one ranger can.',
  'Somebody will name a recruit after {callsign}.',
  '{rank} {name}: {kills} of theirs, and then one of ours.',
  'No one saw {callsign} fall. Everyone noticed the empty seat.',
  '{name} knew the odds and jumped anyway.',
  'Gone north. Not coming back. {callsign}.',
  'The last thing {name} did was the job.',
];

export const EPITAPHS_LOST: readonly string[] = [
  '{name} was moved east before anyone could come. Never recovered.',
  'We knew where {callsign} was held. We were too late.',
  'P.O.W. - never recovered. {rank} {name} is still owed a rescue.',
];
